import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router";
import { useDispatch, useSelector } from "react-redux"; 
import { Box, LinearProgress, Backdrop, Container, Alert, AlertTitle, Button } from "@mui/material";
import UpdateIcon from '@mui/icons-material/Update';

import { validateUserSession } from "./actions/auth_actions";

// Import your new route protectors
import { PrivateRoute, PublicRoute } from "./components/Common/ProtectedRoutes";

import "./App.css";
import Forget from "./components/Auth/Forget";
import {Login} from "./components/Auth/Login";
import Unsub from "./components/Auth/Unsub";
import Maintenance from "./components/Main/Maintenance";
import ApplicationTracking from "./components/Auth/ApplicationTracking";
import Signup from "./components/Auth/Signup";
import Help from "./components/Help";
import BottomBar from "./components/Common/BottomBar";
import { RestoreScroll } from "./components/Common/RestoreScroll";
import Landing from "./components/Landing";
import Main from "./components/Main";
import config from "./config/";
import * as ROUTES from "./constants/routes";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

const App = () => {
  const dispatch = useDispatch();
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const isAuthenticated = useSelector((state) => state.auth.authenticated);
  const doingMaintnance = config.doingMaintnance;

useEffect(() => {
  serviceWorkerRegistration.register({
    onUpdate: (registration) => {
      setNewVersionAvailable(true);
      setWaitingWorker(registration.waiting);
    }
  });
}, []);

  const validateUser = useCallback(() => {
    // We check for `null` which can mean "undetermined"
    if (isAuthenticated !== false) {
      dispatch(validateUserSession());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    validateUser();
    const interval = setInterval(validateUser, config.DEFAULT_SESSIONCHECK_TIME);
    
    return () => {
      clearInterval(interval);
    };
  }, [validateUser]);

  const handleUpdate = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    setNewVersionAvailable(false);
    window.location.reload();
  };
  
  // The DefaultRoute logic is now handled by redirecting from "/"
  // The NotFoundRedirect logic is now handled by the "*" path
  
  // While we wait to determine auth status, show a loading bar.
  // `isAuthenticated` will be `undefined` or `null` on initial load.
  if (isAuthenticated == null) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }
  return (
    <div>
        {doingMaintnance ? (
          <Maintenance />
        ) : (
          <>
            {newVersionAvailable && (
              <Backdrop
                open={newVersionAvailable}
                sx={{ zIndex: (theme) => theme.zIndex.tooltip + 1, flexDirection: 'column' }}
              >
                <Container maxWidth="sm">
                  <Alert
                    severity="info"
                    icon={<UpdateIcon fontSize="inherit" />}
                    action={<Button color="inherit" size="small" onClick={handleUpdate} sx={{ fontWeight: 'bold' }}>RELOAD</Button>}
                    sx={{ alignItems: 'center' }}
                  >
                    <AlertTitle>Update Available</AlertTitle>
                    A new version is available! Please reload to continue.
                  </Alert>
                </Container>
              </Backdrop>
            )}
            <Help isAuthenticated={isAuthenticated}/>
            <RestoreScroll />
            <BottomBar />
            <Routes>
              {/* Default route redirects based on auth status */}
              <Route path={ROUTES.DEFAULT} element={isAuthenticated ? <Navigate to={ROUTES.APP} replace /> : <Navigate to={ROUTES.LOGIN} replace />} />

              {/* --- Protected Routes (User must be logged in) --- */}
              <Route element={<PrivateRoute authStatus={isAuthenticated} />}>
                <Route path={ROUTES.APP + "/*"} element={<Main />} />
                {/* Add other private routes here */}
              </Route>

              {/* --- Public Routes (User must be logged out) --- */}
              <Route element={<PublicRoute authStatus={isAuthenticated} />}>
                <Route path={ROUTES.LOGIN} element={<Login />} />
                <Route path={ROUTES.SIGNUP} element={<Signup />} />
                <Route path={ROUTES.FORGOT} element={<Forget />} />
                <Route path={ROUTES.HELLO} element={<Landing />} />
                <Route path={ROUTES.UNSUB} element={<Unsub />} />
              </Route>
              
              {/* This is a public-facing but accessible route regardless of auth */}
              <Route path={ROUTES.APPLICATION} element={<ApplicationTracking />} />
              
              {/* Catch-all for 404 - redirects to a safe page */}
              <Route path="*" element={<Navigate to={isAuthenticated ? ROUTES.APP : ROUTES.LOGIN} replace />} />
            </Routes>
          </>
        )}
      </div>
    );
  }

// No more mapStateToProps or mapDispatchToProps!
// The connect and withStyles HOCs are gone.
export default App;